<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";

const { t } = useI18n();
const route = useRoute();
const { trackEvent } = useAnalytics();

// Page-local dismiss only — never persisted. Each fresh visit (reload, route
// change, or navigation between listings) resets the bar so the CTA is offered
// again. Reusing the apartment route component across listing IDs keeps state,
// so the watcher below resets it explicitly.
const dismissed = ref(false);

// Reveal trigger: the inline "Book now" button under the photo grid
// (#apartment-inline-cta). The sticky bar appears only after that button has
// scrolled above the viewport — until then the inline CTA is visible and a
// duplicate floating bar would be redundant.
const visible = ref(false);
let observer: IntersectionObserver | null = null;

function dismiss() {
  dismissed.value = true;
  trackEvent("booking_widget_dismiss", { placement: "apartment_sticky" });
}

function scrollToQuote(event: MouseEvent) {
  const target = document.getElementById("booking-quote-widget");
  if (target) {
    event.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  trackEvent("booking_widget_click", {
    placement: "apartment_sticky",
    channel: "anchor",
  });
}

watch(
  () => route.fullPath,
  () => {
    dismissed.value = false;
  },
);

onMounted(() => {
  const trigger = document.getElementById("apartment-inline-cta");
  if (!trigger) return;
  observer = new IntersectionObserver(
    ([entry]) => {
      // Show when the inline CTA is offscreen AND scrolled above the viewport
      // (top < 0). top > 0 would mean it is below the fold — bar stays hidden.
      visible.value =
        !entry.isIntersecting && entry.boundingClientRect.top < 0;
    },
    { threshold: 0 },
  );
  observer.observe(trigger);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});

const show = computed(() => visible.value && !dismissed.value);
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0"
  >
    <aside
      v-if="show"
      role="complementary"
      :aria-label="t('stickyApartment.ariaLabel')"
      class="fixed inset-x-0 bottom-0 z-[999] pointer-events-none sm:hidden"
    >
      <div
        class="pointer-events-auto mx-auto bg-slate-900/95 shadow-[0_-8px_30px_rgb(0,0,0,0.12)] backdrop-blur-md dark:bg-slate-950/95"
        style="padding-bottom: env(safe-area-inset-bottom)"
      >
        <div class="flex items-center gap-3 px-4 py-3">
          <a
            href="#booking-quote-widget"
            class="group inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-red-500 px-5 py-2.5 font-body text-sm font-semibold uppercase tracking-[0.12em] text-white shadow-sm transition-all hover:bg-red-600 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            @click="scrollToQuote"
          >
            <Icon name="lucide:calendar-check" class="size-5 shrink-0" aria-hidden="true" />
            <span>{{ t('stickyApartment.cta') }}</span>
          </a>

          <button
            type="button"
            :aria-label="t('stickyApartment.close')"
            class="shrink-0 rounded-full p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            @click="dismiss"
          >
            <Icon name="lucide:x" class="size-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </aside>
  </Transition>
</template>
